import { useMutation, useQuery } from "@apollo/client";
import { chain, ObjectChain } from "lodash";
import React from "react";
import { refetchHandles } from "data";
import { Viewer } from "components/auth/types";
import {
  createActivityContainer,
  createActivityContainerVariables,
  interestActivityPage,
} from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { CREATE_ACTIVITY } from "./queries";
import { ActivityContent } from "./activity-content";
import QUERY_INTEREST_DATA from "./query-interest-data.gql";

type LocationState = {
  addNoteIndex: number;
  logCallIndex: number;
  sendMessageIndex: number;
};


const parseForm = (form: HTMLFormElement) => (
  (chain(form.elements))
    .filter((e: any) => e.type !== "submit")
    .reduce((obj: any, e: any) => {
      if (e.type === "checkbox") {
        obj[e.name] = e.checked;
      } else if (e.type === "radio") {
        if (e.checked) {
          if (e.value === "true") obj[e.name] = true;
          else if (e.value === "false") obj[e.name] = false;
          else obj[e.name] = e.value;
        }
      } else {
        obj[e.name] = e.value;
      }
      return obj;
    }, {}) as ObjectChain<any>)
  .mapValues((val: any) => (typeof val === "undefined" || val === "Select" ? null : val))
  .value();

const onSubmitNewActivity = (
  interestID: string,
  createActivity: any,
  openSnackbar: any,
) => (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  const form = document.getElementById("activityForm") as HTMLFormElement | null;
  if (!form) return;
  const formData = parseForm(form);

  if (!formData.note) return;

  const request = createActivity({
    variables: {
      data: {
        resourceId: interestID,
        title: "Note",
        type: "note",
        resourceTable: "interests",
        note: formData.note || null,
      },
    },
  });

  request.then(() => {
    openSnackbar({ message: "Success", status: "success" });
    if (form === null) return;
    form.reset();
    refetchHandles.activityFeed();
  });

  request.catch((err: Error) => {
    logger.sendError(err);
    openSnackbar({ message: "activity creation failed. Please try again.", status: "error" });
  });
};

type NavigateFunctionLike = (to: string, options?: { replace?: boolean; state?: any }) => void;
type LocationLike = { pathname: string; state?: LocationState };

type Props = {
  interestID: string;
  interestListID: string;
  viewer: Viewer;
  path?: string;
  location?: LocationLike;
  navigate?: NavigateFunctionLike;
}

const ActivityPage: React.FC<Props> = (props: Props) => {
  const { openSnackbar } = useSnackbar();
  const query = useQuery<interestActivityPage>(QUERY_INTEREST_DATA, {
    variables: { interestId: props.interestID },
  });

  const [createActivity] = useMutation<createActivityContainer, createActivityContainerVariables>(
    CREATE_ACTIVITY,
  );

  let addNoteIndex = -1;
  let logCallIndex = -1;
  let sendMessageIndex = -1;
  if (props.location && props.location.state) {
    ({ addNoteIndex, logCallIndex, sendMessageIndex } = props.location.state as LocationState);
  }

  // clears location state to prevent refreshing causing the navigation actions to trigger
  // upon loading of slideout menu
  const clearLocationState = () => {
    if (props.location && props.navigate) {
      props.navigate(props.location.pathname, { state: {} });
    }
  };

  return (
    <ActivityContent
      interestListID={props.interestListID}
      interestID={props.interestID}
      onSubmit={onSubmitNewActivity(props.interestID, createActivity, openSnackbar)}
      interest={query.data?.interest || {}}
      isLoading={query.loading}
      isLoadingInterest={query.loading}
      isLoadingPdf={false}
      viewer={props.viewer}

      addNoteIndex={addNoteIndex}
      logCallIndex={logCallIndex}
      sendMessageIndex={sendMessageIndex}

      cleanupNavigateAction={clearLocationState}
    />
  );
};

export default ActivityPage;
